import { concat, isEmpty, partition } from 'lodash';
import { createSelector } from 'reselect';
import Logger from '../../../utils/Logger';
import {
  formatDate,
  formatISO8601,
  formatTime,
  getTime,
} from '../../../utils/TimeUtils';
import {
  sortByTimeNewestFirst,
  sortByTimeOldestFirst,
} from '../../../utils/SortUtils';
import {
  LABORATORY_PAGE_NOTIFICATION,
  notificationIdentity,
} from '../../../definitions/NotificationConstants';
import {
  TYPE_DEFAULT,
  TYPE_FIMLAB,
  TYPE_HEARING_DEVICE_MODELING,
  TYPE_HEARING_DEVICE_FIXING,
  TYPE_SLEEP_CLINIC,
  TYPE_GTI,
  TYPE_REFERRAL,
  TYPE_COXA_TREATMENT_PERIOD,
  TYPE_VIDEO_CONFERENCE,
} from '../../../definitions/TimelineItems';
import {
  FEATURE_FIMLAB,
  FEATURE_IMAGING,
  isFeatureOn,
} from '../../../definitions/Config';
import {
  timelineSelector,
  fimlabSelector,
  notificationSelector,
  appointmentSelector,
  questionnaireSelector,
} from '../../../data/selector';
import { PlaceHolderIds } from '../../../components/PagePlaceholder';
import { shouldShowPlaceholder } from '../../../utils/Functions';
import { GTIFilter } from '../../../definitions/GtiConstants';
import { isRemoteVisit } from '../../../utils/TimelineUtils';

let { id: module_id } = module;
const logger = new Logger(module_id);

var moment = require('moment');

const biobankTests = [
  '97000',
  '97001',
  '97002',
  '97003',
  '97004',
  '97005',
  '97010',
  '97011',
  '97012',
  '97013',
  '97014',
  '97015',
  '97016',
  '97017',
  '97018',
  '97019',
  '97020',
  '97021',
];

const hearingDeviceFixingPairs = {
  PKO: ['HUOLTO_PKO'],
};

const hearingDeviceModelingPairs = {
  PKU: ['HUOLTO_PKU'],
};

const sleepClinicIds = [
  '1.2.246.10.8265978.10.1.275',
  '1.2.246.10.8265978.10.1.441',
  '1.2.246.10.8265978.10.1.437',
];

const coxaTreatmentPeriodUnits = [
  '1.2.246.10.18612099.10.1.2',
  '1.2.246.10.18612099.10.1.10',
  '1.2.246.10.18612099.10.1.19',
  '1.2.246.10.18612099.10.1.20',
];

const isPairOf = (item, pairs) => {
  return Object.keys(pairs).some(
    (key) =>
      new RegExp(`(${key})`, 'i').test(item.department) &&
      new RegExp(`(${pairs[key].join('|')})`, 'i').test(item.resource)
  );
};

const isHearingDeviceModeling = (item) =>
  isPairOf(item, hearingDeviceModelingPairs);

const isHearingDeviceFixing = (item) =>
  isPairOf(item, hearingDeviceFixingPairs);

const isSleepClinic = (item) => sleepClinicIds.includes(item.oid);

const isCoxaTreatmentPeriod = (item) =>
  coxaTreatmentPeriodUnits.includes(item.oid) && item.visitType === null;

const isReferral = (item) => item.type === 'REFERRAL';

export const determineAndAddType = (item) => {
  const isImagingOn = isFeatureOn(FEATURE_IMAGING);

  if (isReferral(item)) {
    return addType(item, TYPE_REFERRAL);
  }

  if (isImagingOn && item?.config?.type === 'imaging') {
    if (item?.config?.subtype) {
      return addType(item, item?.config?.subtype);
    }
    return addType(item, TYPE_DEFAULT);
  }

  if (isHearingDeviceModeling(item)) {
    return addType(item, TYPE_HEARING_DEVICE_MODELING);
  }

  if (isHearingDeviceFixing(item)) {
    return addType(item, TYPE_HEARING_DEVICE_FIXING);
  }

  if (isSleepClinic(item)) {
    return addType(item, TYPE_SLEEP_CLINIC);
  }

  if (GTIFilter(item)) {
    return addType(item, TYPE_GTI);
  }

  if (isCoxaTreatmentPeriod(item)) {
    return addType(item, TYPE_COXA_TREATMENT_PERIOD);
  }
  if (isRemoteVisit(item)) {
    return addType(item, TYPE_VIDEO_CONFERENCE);
  }

  return addType(item, TYPE_DEFAULT);
};

/**
 * Adds item type as it's property.
 * @param item appointment to modify
 * @param type given type
 */
const addType = (item, type) => ({
  ...item,
  type,
});

/**
 * Determines whether timeline event can be rescheduled.
 * @param appointments appointment state data
 */
const addCanBeRescheduled = (appointments) => (item) => {
  let rescheduleValue = appointments
    .filter((app) => app.appointmentId === item.id)
    .filter(
      (app) =>
        app.modify === 'CAN_RESCHEDULE_ONCE' ||
        app.modify === 'CAN_RESCHEDULE_MULTIPLE'
    )
    .map((app) => app.modify);
  if (rescheduleValue.length > 0) {
    rescheduleValue = rescheduleValue[0];
  } else {
    rescheduleValue = false;
  }
  return {
    ...item,
    //canBeRescheduled: appointments.filter(app => app.modify === "CAN_RESCHEDULE_ONCE" || app.modify === "CAN_RESCHEDULE_MULTIPLE").map(app => app.appointmentId).indexOf(item.id) >= 0
    canBeRescheduled: rescheduleValue,
  };
};

/**
 * Select timeline notifications to be shown in the timeline page
 * and give them related additions reduced from other data.
 *
 * @param timelineStore
 * @param appointmentStore
 */
const selectTimelineNotificationData = (timelineStore, appointmentStore) => {
  // timelineStore data might be null and not empty,
  // return empty array.
  if (!timelineStore.data) return [];
  return (
    timelineStore.data
      // filter items which are unapproved and active (incoming careEvent.state active is mapped as Booked in api-gateway)
      .filter(
        (item) =>
          // In case of Referral, check notify -value. Referrals may have null visitType, and must be shown even if state is active.
          (item.type !== 'REFERRAL' &&
            !item.approved &&
            item.state === 'BOOKED' &&
            item.visitType !== 'VISIT') ||
          (item.type === 'REFERRAL' && item.notify)
      )
      // add type of timeline
      .map(determineAndAddType)
      // add whether item can be rescheduled (this is deduced from appointments)
      .map(addCanBeRescheduled(appointmentStore.data))
  );
};

/**
 * Determines whether given notification is related to laboratory items.
 *
 * @param notification
 * @returns {boolean}
 */
export const isLaboratoryNotification = (notification) => {
  return (
    notification[notificationIdentity].indexOf(LABORATORY_PAGE_NOTIFICATION) >=
    0
  );
};

/**
 * Provides bidirectional mapping from laboratory event id
 * to corresponding notification key.
 *
 * @param data The laboratory event
 * @returns {string} The corresponding laboratory event key.
 */
export const laboratoryNotificationIdFromEvent = (data) => {
  return LABORATORY_PAGE_NOTIFICATION + '_' + data.groupId;
};

/**
 * Returns notifcation id from notification (the key is mapped as part of the notification backend key
 * so whis way we get the number only).
 *
 * @param notification The notification item
 * @returns {string} the id of notification
 */
export const laboratoryNotificationIdFromNotificationItem = (notification) => {
  return notification[notificationIdentity].substring(
    LABORATORY_PAGE_NOTIFICATION.length + 1
  );
};

/**
 * Determines whether Fimlab event (laboratory visit) has an active notification.
 *
 * @param id {String} id of fimlab event
 * @param notifications {array} Notifications from state
 */

const findFimlabNotification = (id, notifications) => {
  return (
    notifications
      // filter notifications matching wanted key
      .filter(isLaboratoryNotification)
      // filter items with matching id to item
      .filter(
        (notification) =>
          laboratoryNotificationIdFromNotificationItem(notification) ===
          String(id)
      )[0]
  );
};

export const laboratoryNotificationGroupIdFromDataItem = (item) => {
  let groupId =
    item.processId +
    '_' +
    item.timeOfRequest +
    '_' +
    item.requestedCollectionDate;
  return groupId;
};

export const _selectFimlabNotificationData = (
  fimlabSelector,
  notificationSelector
) => {
  const reducerFunction = (events, item) => {
    let backendNotification = findFimlabNotification(
      laboratoryNotificationGroupIdFromDataItem(item),
      notificationSelector.data
    );
    if (isEmpty(backendNotification)) {
      return events;
    }

    return [
      ...events,
      {
        ...item,
        type: TYPE_FIMLAB,
        notification: backendNotification,
      },
    ];
  };

  if (!fimlabSelector.data || isEmpty(fimlabSelector.data)) {
    return [];
  }
  return fimlabSelector.data.reduce(reducerFunction, []);
};

/**
 * Select fimlab laboratory events to be shown in the notification view.
 *
 * Function will also add type to notification.
 * Event id is compared to notifications in fimlabEventHasNotification
 *
 * @param fimlabStore  Fimlab events from state
 * @param notificationStore Notifications from state
 */
export const selectFimlabNotificationData = createSelector(
  [fimlabSelector, notificationSelector],
  _selectFimlabNotificationData
);

/**
 * Takes data from backend and formats it more suitable to
 * use in react components.
 *
 * @param data
 */
export const mapFimlabData = (data) => {
  const formatDates = (item) => {
    return {
      ...item,
      collectionTimeWindowStart: formatDate(item.collectionTimeWindowStart),
      collectionTimeWindowEnd: formatDate(item.collectionTimeWindowEnd),
      timeOfRequest: formatDate(item.timeOfRequest),
      requestedCollectionDateDate: formatDate(item.requestedCollectionDate),
      requestedCollectionDateTime: formatTime(item.requestedCollectionDate),
      date: formatISO8601(item.requestedCollectionDate),
    };
  };

  let filteredData = data.filter(
    (item) =>
      !(
        moment().diff(
          moment(formatDate(item.requestedCollectionDate), 'DD.MM.YYYY'),
          'days'
        ) > 39
      )
  );
  return filteredData.map(formatDates);
};

/**
 * Determines whether notification related data is being fetched from backend.
 * Showing the loader data for the whole view and displaying the resultset when
 * all the data is loaded is better for user.
 *
 * @param state Root state
 * @returns {boolean} Whether notification view related data is fetching
 */
const selectNotificationsFetching = (
  timelineSelector,
  notificationSelector
) => {
  return timelineSelector.isFetching || notificationSelector.isFetching;
};

const selectNotificationData = createSelector(
  [
    timelineSelector,
    notificationSelector,
    appointmentSelector,
    fimlabSelector,
    selectFimlabNotificationData,
    questionnaireSelector,
  ],
  (
    timelineSelector,
    notificationSelector,
    appointmentSelector,
    fimlabSelector,
    fimlabNotificationData,
    questionnaireSelector
  ) => {
    //TODO: error handling
    let error = null;

    // determine whether backend operations are updating in such a way that
    // corresponding data should not yet be shown.
    let isFetching = selectNotificationsFetching(
      timelineSelector,
      notificationSelector
    );

    let questionnaireLinks = selectQuestionnairesWithLinks(
      questionnaireSelector
    );

    // select timeline items to show in notifications
    let timelineNotifications = selectTimelineNotificationData(
      timelineSelector,
      appointmentSelector
    ).map(addQuestionnaires(questionnaireLinks));

    // select fimlab items to show in notifications
    let fimlabNotifications = mapFimlabData(fimlabNotificationData);

    // merge items and sort by time
    let notifications = concat(timelineNotifications, fimlabNotifications).sort(
      sortByTimeOldestFirst('date')
    );

    return {
      notifications,
      isFetching,
      error,
    };
  }
);

const selectTimelineData = (timelineStore, appointmentStore) => {
  // timelineStore data might be null and not empty,
  // return empty array.
  if (!timelineStore.data) return [];

  return timelineStore.data
    .filter(
      (item) =>
        (item.type !== 'REFERRAL' && item.approved) ||
        (item.type === 'REFERRAL' && !item.notify)
    )
    .map(determineAndAddType)
    .map(addCanBeRescheduled(appointmentStore.data));
};

export const selectFimlabData = (fimlabSelector, notificationSelector) => {
  if (notificationSelector.isFetching || notificationSelector.error) {
    return [];
  } else {
    let fimlabNotificationIds = notificationSelector.data
      .filter(isLaboratoryNotification)
      .map(laboratoryNotificationIdFromNotificationItem);
    return fimlabSelector.data
      .filter((item) => {
        let groupId = laboratoryNotificationGroupIdFromDataItem(item);
        return fimlabNotificationIds.indexOf(String(groupId)) === -1;
      })
      .map((item) => addType(item, TYPE_FIMLAB));
  }
};

const selectTimelineFetching = (timelineSelector, fimlabSelector) => {
  if (isFeatureOn(FEATURE_FIMLAB)) {
    return timelineSelector.isFetching || fimlabSelector.isFetching;
  } else {
    return timelineSelector.isFetching;
  }
};

const selectTimelineErrors = (timelineSelector, fimlabSelector) => {
  return {
    fimlabError: fimlabSelector.error,
    timelineError: timelineSelector.error,
  };
};

const selectUpcomingAndPastEvents = createSelector(
  [
    timelineSelector,
    fimlabSelector,
    notificationSelector,
    appointmentSelector,
    questionnaireSelector,
  ],
  (
    timelineSelector,
    fimlabSelector,
    notificationSelector,
    appointmentSelector,
    questionnaireSelector
  ) => {
    logger.info(
      'calling selectUpcomingAndPastEvents',
      timelineSelector,
      fimlabSelector,
      notificationSelector
    );
    const baseResult = {
      isFetching: null,
      error: null,
      upcomingEvents: [],
      pastEvents: [],
    };

    let isFetching = selectTimelineFetching(timelineSelector, fimlabSelector);

    if (isFetching) {
      return {
        ...baseResult,
        isFetching,
      };
    }

    let error = selectTimelineErrors(timelineSelector, fimlabSelector);

    // collect all events here
    let allEvents = [];

    // select timeline items
    allEvents = allEvents.concat(
      selectTimelineData(timelineSelector, appointmentSelector)
    );

    if (isFeatureOn(FEATURE_FIMLAB)) {
      // if fimlab feature is on, select fimlab items
      let fimlabEvents = mapFimlabData(
        selectFimlabData(fimlabSelector, notificationSelector)
      );
      allEvents = allEvents.concat(fimlabEvents);
    }

    let questionnaireLinks = selectQuestionnairesWithLinks(
      questionnaireSelector
    );

    allEvents = allEvents.map(addQuestionnaires(questionnaireLinks));

    // get events partitioned to upcoming and past events.
    let partitionedEvents = partition(allEvents, (item) => {
      if (item.type === TYPE_FIMLAB) {
        return getTime(
          moment(item.collectionTimeWindowEnd, 'DD.MM.YYYY').add(1, 'days')
        ).isBefore(getTime().subtract(2, 'hours'));
      } else {
        return getTime(item.date).isBefore(getTime().subtract(2, 'hours'));
      }
    });

    // take arrays of the partitioned events and sort.
    // ascending: newest first
    let upcomingEvents = partitionedEvents[1].sort(
      sortByTimeOldestFirst('date')
    );
    // descending: oldest first
    let pastEvents = partitionedEvents[0].sort(sortByTimeNewestFirst('date'));

    return {
      isFetching,
      error,
      upcomingEvents: upcomingEvents,
      // note that the first 15 of past events are shown.
      pastEvents: pastEvents,
    };
  }
);

const addQuestionnaires = (questionnaireLinks) => (item) => {
  let result = questionnaireLinks.filter(
    (x) => x?.fhirAppointmentId === item.id || x?.timelineEventId === item.id
  );

  if (result) {
    item.questionnaire = result;
  }

  return item;
};

const selectQuestionnairesWithLinks = (questionnaireSelector) => {
  let data = questionnaireSelector.data;
  let links = [];
  if (
    data.medixine &&
    data.medixine.active &&
    data.medixine.started &&
    data.medixine.completed
  ) {
    links = links.concat(
      data.medixine.active.filter((x) => x.fhirAppointmentId),
      data.medixine.started.filter((x) => x.fhirAppointmentId),
      data.medixine.completed.filter((x) => x.fhirAppointmentId),
      questionnaireSelector?.assignedForms.filter((x) => x.timelineEventId)
    );
  }
  if (data.omavointi && data.omavointi.questionnaires) {
    links = [
      ...links,
      ...data.omavointi.questionnaires.filter((x) => x.fhirAppointmentId),
    ];
  }
  return links;
};

const showPlaceHolderOnTimeline = createSelector(
  [selectUpcomingAndPastEvents, selectNotificationData],
  (selectUpcomingAndPastEvents, selectNotificationData) => {
    let {
      upcomingEvents,
      pastEvents,
      isFetching,
      error: eventsError,
    } = selectUpcomingAndPastEvents;
    let {
      notifications,
      isFetching: notificationsIsFetching,
      error: notificationsError,
    } = selectNotificationData;

    if (isFetching || notificationsIsFetching) {
      return PlaceHolderIds.LOADER;
    }

    let error = eventsError.timelineError || eventsError.fimlabError;
    let show =
      !error &&
      notificationsError == null &&
      isEmpty(upcomingEvents) &&
      isEmpty(pastEvents) &&
      isEmpty(notifications);

    return shouldShowPlaceholder(show, PlaceHolderIds.TIMELINE);
  }
);

export {
  selectNotificationData,
  selectUpcomingAndPastEvents,
  selectQuestionnairesWithLinks,
  showPlaceHolderOnTimeline,
};
